// Shared invoice print builders — used by both the admin Invoices page and
// the hostel portal's own Invoices page, so the two always look identical.
// Follows the same "build HTML string, open a blank window, print" pattern
// already used elsewhere in this app (see HostelAdmissionsPortal PrintView).

const HAZZA_LOGO = window.location.origin + '/assets/hazza-logo.png';

const fmtMoney = (n) => `PKR ${Number(n || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : d.toLocaleDateString('en-PK', { year: 'numeric', month: 'short', day: '2-digit' });
};

const BASE_STYLES = `
  * { box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 32px; color: #1a2340; background: #fff; }
  .sheet { max-width: 900px; margin: 0 auto; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #0e1e45; padding-bottom: 16px; margin-bottom: 22px; gap: 16px; }
  .brand { display: flex; align-items: center; gap: 12px; }
  .brand img.logo { height: 50px; }
  .brand-text strong { display: block; font-size: 17px; color: #0e1e45; }
  .brand-text span { display: block; font-size: 11.5px; color: #6b7280; margin-top: 2px; }
  .hostel-pic { width: 44px; height: 44px; border-radius: 8px; object-fit: cover; border: 1px solid #e4e8f4; margin-left: 10px; }
  .meta { text-align: right; font-size: 12.5px; color: #5b6785; }
  .meta strong { color: #1a2340; }
  .meta .inv-no { font-size: 15px; font-weight: 800; color: #c48a00; margin-bottom: 4px; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-bottom: 20px; }
  .info-box { background: #f7f8fc; border-radius: 10px; padding: 14px 16px; font-size: 12.5px; }
  .info-box h4 { margin: 0 0 8px; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; color: #94a0bf; }
  .info-box div { margin-bottom: 3px; }
  table.items { width: 100%; border-collapse: collapse; margin: 18px 0; }
  table.items th { background: #0e1e45; color: #fff; padding: 9px 12px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: .03em; }
  table.items td { padding: 9px 12px; border-bottom: 1px solid #e4e8f4; font-size: 12.5px; }
  table.items tr.total-row td { font-weight: 800; font-size: 15px; border-top: 2px solid #0e1e45; border-bottom: none; color: #0e1e45; }
  .amt { text-align: right; white-space: nowrap; }
  .foot-note { margin-top: 26px; font-size: 11px; color: #94a0bf; text-align: center; }
  @media print { body { padding: 12px; } }
`;

const PRINT_BUTTON = `
  <button onclick="window.print()" class="no-print"
    style="position:fixed;top:16px;right:16px;padding:10px 20px;background:#0e1e45;color:#fff;
    border:none;border-radius:8px;cursor:pointer;font-size:13px;font-weight:700;z-index:999;
    box-shadow:0 4px 14px rgba(14,30,69,0.3);">🖨 Print</button>
  <style>@media print { .no-print { display: none !important; } }</style>`;

function shell(title, bodyHtml, extraStyles = '') {
  return `<!DOCTYPE html><html><head><title>${title}</title><meta charset="utf-8"><style>${BASE_STYLES}${extraStyles}</style></head><body>${PRINT_BUTTON}${bodyHtml}</body></html>`;
}

// Wide tables (the consolidated bulk invoice, especially with the optional
// attendance/meal summary columns turned on) don't fit a portrait A4 page
// at the normal font size — this forces landscape + a wider sheet + tighter
// table cells so columns compress instead of running off the page edge.
const WIDE_STYLES = `
  .sheet { max-width: 1500px; }
  table.items.wide th, table.items.wide td { padding: 6px 8px; font-size: 10.5px; }
  @media print { @page { size: A4 landscape; margin: 10mm; } }
`;

function openAndPrint(html, autoPrint = true) {
  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
  if (autoPrint) setTimeout(() => { w.focus(); w.print(); }, 450);
}

// ── CSV export (bulk invoices, optionally with attendance/meal detail) ──
function toCsvValue(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename, rows) {
  const csv = rows.map(r => r.map(toCsvValue).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function attendanceDetailRows(admissionId, attendanceRows) {
  return attendanceRows.filter(r => String(r.admission_id) === String(admissionId));
}
function mealDetailRows(admissionId, mealRows) {
  return mealRows.filter(r => String(r.admission_id) === String(admissionId));
}
function mealCounts(admissionId, mealRows) {
  const rows = mealDetailRows(admissionId, mealRows);
  return {
    breakfast: rows.filter(r => r.breakfast).length,
    lunch: rows.filter(r => r.lunch).length,
    dinner: rows.filter(r => r.dinner).length,
  };
}

function detailTablesHtml({ attendanceRows = [], mealRows = [], includeAttendance, includeMeals }) {
  let html = '';
  if (includeAttendance && attendanceRows.length) {
    html += `
      <h4 style="margin:20px 0 8px;font-size:12px;text-transform:uppercase;letter-spacing:.04em;color:#94a0bf;">Attendance Detail</h4>
      <table class="items">
        <thead><tr><th>Date</th><th>Status</th><th>Marked By</th></tr></thead>
        <tbody>${attendanceRows.map(r => `<tr><td>${fmtDate(r.attendance_date)}</td><td>${r.status}</td><td>${r.marked_by_name || r.marked_by || '—'}</td></tr>`).join('')}</tbody>
      </table>`;
  }
  if (includeMeals && mealRows.length) {
    html += `
      <h4 style="margin:20px 0 8px;font-size:12px;text-transform:uppercase;letter-spacing:.04em;color:#94a0bf;">Meal Detail</h4>
      <table class="items">
        <thead><tr><th>Date</th><th>Breakfast</th><th>Lunch</th><th>Dinner</th></tr></thead>
        <tbody>${mealRows.map(r => `<tr><td>${fmtDate(r.meal_date)}</td><td>${r.breakfast ? 'Yes' : 'No'}</td><td>${r.lunch ? 'Yes' : 'No'}</td><td>${r.dinner ? 'Yes' : 'No'}</td></tr>`).join('')}</tbody>
      </table>`;
  }
  return html;
}

function summaryFieldsHtml(invoice, { includeDaysSummary, includeMealsSummary, mealRows = [] } = {}) {
  if (!includeDaysSummary && !includeMealsSummary) return '';
  const mc = includeMealsSummary ? { breakfast: mealRows.filter(r => r.breakfast).length, lunch: mealRows.filter(r => r.lunch).length, dinner: mealRows.filter(r => r.dinner).length } : null;
  return `
    <div class="info-box" style="margin:16px 0;">
      <h4>Attendance / Meal Summary</h4>
      <div style="display:flex;gap:20px;flex-wrap:wrap;">
        ${includeDaysSummary ? `<span>Present: <strong>${invoice.present_days ?? 0}</strong></span><span>Absent: <strong>${invoice.absent_days ?? 0}</strong></span><span>Total Days: <strong>${invoice.total_days ?? 0}</strong></span>` : ''}
        ${includeMealsSummary ? `<span>Breakfast: <strong>${mc.breakfast}</strong></span><span>Lunch: <strong>${mc.lunch}</strong></span><span>Dinner: <strong>${mc.dinner}</strong></span>` : ''}
      </div>
    </div>`;
}

// ── Individual invoice — one resident, itemized ─────────────────────
export function printIndividualInvoice(invoice, opts = {}) {
  const lineItems = Array.isArray(invoice.line_items) ? invoice.line_items : [];
  const rows = lineItems.map(li => `
    <tr>
      <td>${li.label}${li.detail ? `<br><span style="color:#94a0bf;font-size:11px;">${li.detail}</span>` : ''}</td>
      <td class="amt">${fmtMoney(li.amount)}</td>
    </tr>`).join('');

  const body = `
    <div class="sheet">
      <div class="head">
        <div class="brand">
          <img class="logo" src="${HAZZA_LOGO}" alt="Hazza Institute of Technology" />
          <div class="brand-text">
            <strong>Hazza Institute of Technology</strong>
            <span>Hostel Invoice</span>
          </div>
          ${invoice.profile_image ? `<img class="hostel-pic" src="${invoice.profile_image}" alt="${invoice.hostel_name}" />` : ''}
        </div>
        <div class="meta">
          <div class="inv-no">${invoice.invoice_number}</div>
          <div>Issued: <strong>${fmtDate(invoice.created_at || new Date())}</strong></div>
          <div>Period: <strong>${fmtDate(invoice.period_start)} – ${fmtDate(invoice.period_end)}</strong></div>
        </div>
      </div>

      <div class="info-grid">
        <div class="info-box">
          <h4>Resident</h4>
          <div><strong>${invoice.full_name}</strong></div>
          <div>Father: ${invoice.father_name || '—'}</div>
          <div>CNIC: ${invoice.cnic || '—'}</div>
          <div>Contact: ${invoice.contact_no || '—'}</div>
        </div>
        <div class="info-box">
          <h4>Hostel</h4>
          <div><strong>${invoice.hostel_name}</strong></div>
          <div>In-charge: ${invoice.incharge_name || '—'}</div>
          <div>Address: ${invoice.hostel_address || '—'}</div>
          <div>Billing mode: ${invoice.billing_mode === 'daily_attendance' ? 'Daily rate' : 'Monthly flat'}</div>
        </div>
      </div>

      <table class="items">
        <thead><tr><th>Description</th><th class="amt">Amount</th></tr></thead>
        <tbody>
          ${rows || '<tr><td colspan="2" style="text-align:center;color:#94a0bf;">No charges for this period</td></tr>'}
          <tr class="total-row"><td>Total Due</td><td class="amt">${fmtMoney(invoice.total_amount)}</td></tr>
        </tbody>
      </table>

      ${summaryFieldsHtml(invoice, opts)}
      ${detailTablesHtml(opts)}

      <div class="foot-note">Generated by Hazza Institute of Technology — Hostel Management System</div>
    </div>`;

  openAndPrint(shell(`Invoice ${invoice.invoice_number}`, body), opts.autoPrint !== false);
}

// Opens the same invoice sheet without auto-triggering the print dialog —
// for a "View" button distinct from "Print", still with an on-page Print
// button available (the shell always includes one).
export function viewIndividualInvoice(invoice, opts = {}) {
  return printIndividualInvoice(invoice, { ...opts, autoPrint: false });
}

// ── Individual invoice — CSV ─────────────────────────────────────────
export function exportIndividualInvoiceCSV(invoice, opts = {}) {
  const { attendanceRows = [], mealRows = [], includeAttendance, includeMeals, includeDaysSummary, includeMealsSummary } = opts;
  const lineItems = Array.isArray(invoice.line_items) ? invoice.line_items : [];
  const rows = [];
  rows.push([`Invoice ${invoice.invoice_number}`]);
  rows.push([`Resident: ${invoice.full_name}`, `Hostel: ${invoice.hostel_name}`]);
  rows.push([`Period: ${fmtDate(invoice.period_start)} to ${fmtDate(invoice.period_end)}`]);
  rows.push([]);
  rows.push(['Description', 'Detail', 'Amount']);
  lineItems.forEach(li => rows.push([li.label, li.detail || '', li.amount]));
  rows.push(['Total Due', '', invoice.total_amount]);

  if (includeDaysSummary || includeMealsSummary) {
    rows.push([]); rows.push(['Attendance / Meal Summary']);
    if (includeDaysSummary) rows.push(['Present Days', invoice.present_days ?? 0], ['Absent Days', invoice.absent_days ?? 0], ['Total Days', invoice.total_days ?? 0]);
    if (includeMealsSummary) {
      rows.push(['Breakfast Served', mealRows.filter(r => r.breakfast).length]);
      rows.push(['Lunch Served', mealRows.filter(r => r.lunch).length]);
      rows.push(['Dinner Served', mealRows.filter(r => r.dinner).length]);
    }
  }

  if (includeAttendance && attendanceRows.length) {
    rows.push([]); rows.push(['Attendance Detail']);
    rows.push(['Date', 'Status', 'Marked By']);
    attendanceRows.forEach(r => rows.push([fmtDate(r.attendance_date), r.status, r.marked_by_name || r.marked_by || '']));
  }
  if (includeMeals && mealRows.length) {
    rows.push([]); rows.push(['Meal Detail']);
    rows.push(['Date', 'Breakfast', 'Lunch', 'Dinner']);
    mealRows.forEach(r => rows.push([fmtDate(r.meal_date), r.breakfast ? 'Yes' : 'No', r.lunch ? 'Yes' : 'No', r.dinner ? 'Yes' : 'No']));
  }
  downloadCsv(`${invoice.invoice_number}.csv`, rows);
}

// ── Bulk invoice — one table, all residents, grand total ────────────
// Meals/Deduction columns only render when that hostel actually has the
// corresponding billing feature turned on — otherwise every row shows a
// meaningless "PKR 0.00" for a feature the hostel never enabled.
export function printBulkInvoice({ hostel_id, hostelName, hostelProfileImage, period_start, period_end, invoices, grand_total, attendanceRows = [], mealRows = [], includeAttendance, includeMeals, includeDaysSummary, includeMealsSummary, showMeals = true, showDeduction = true, autoPrint = true }) {
  const summaryHeadCells = [
    includeDaysSummary ? '<th class="amt">Present</th><th class="amt">Absent</th><th class="amt">Total Days</th>' : '',
    includeMealsSummary ? '<th class="amt">Breakfast</th><th class="amt">Lunch</th><th class="amt">Dinner</th>' : '',
  ].join('');
  const baseColCount = 5 + (showMeals ? 1 : 0) + (showDeduction ? 1 : 0) + 2; // #, Resident, CNIC, Invoice#, Base, [Meals], [Deduction], Adj., Total
  const colCount = baseColCount + (includeDaysSummary ? 3 : 0) + (includeMealsSummary ? 3 : 0);
  const isWide = colCount > 9;

  const rows = invoices.map((inv, i) => {
    const daysCells = includeDaysSummary
      ? `<td class="amt">${inv.calculation.present_days ?? 0}</td><td class="amt">${inv.calculation.absent_days ?? 0}</td><td class="amt">${inv.calculation.total_days ?? 0}</td>`
      : '';
    const mc = includeMealsSummary ? mealCounts(inv.admission.id, mealRows) : null;
    const mealSummaryCells = includeMealsSummary
      ? `<td class="amt">${mc.breakfast}</td><td class="amt">${mc.lunch}</td><td class="amt">${mc.dinner}</td>`
      : '';
    return `
    <tr>
      <td>${i + 1}</td>
      <td>${inv.admission.full_name}</td>
      <td>${inv.admission.cnic || '—'}</td>
      <td>${inv.invoice_number}</td>
      <td class="amt">${fmtMoney(inv.calculation.base_amount)}</td>
      ${showMeals ? `<td class="amt">${fmtMoney(inv.calculation.meal_amount)}</td>` : ''}
      ${showDeduction ? `<td class="amt">${fmtMoney(-inv.calculation.deduction_amount)}</td>` : ''}
      <td class="amt">${fmtMoney(inv.calculation.adjustment_amount)}</td>
      <td class="amt"><strong>${fmtMoney(inv.calculation.total_amount)}</strong></td>
      ${daysCells}${mealSummaryCells}
    </tr>`;
  }).join('');

  const detailSections = (includeAttendance || includeMeals) ? invoices.map(inv => {
    const admId = inv.admission.id;
    const att = includeAttendance ? attendanceDetailRows(admId, attendanceRows) : [];
    const meals = includeMeals ? mealDetailRows(admId, mealRows) : [];
    if (!att.length && !meals.length) return '';
    return `
      <div style="page-break-inside:avoid;margin-top:14px;">
        <h4 style="margin:0 0 8px;font-size:12.5px;color:#0e1e45;">${inv.admission.full_name} — ${inv.invoice_number}</h4>
        ${detailTablesHtml({ attendanceRows: att, mealRows: meals, includeAttendance, includeMeals })}
      </div>`;
  }).join('') : '';

  const totalRowLeadColspan = baseColCount - 1; // every base column except "Total" itself
  const trailingColspan = colCount - baseColCount; // any summary columns after the Grand Total amount cell

  const body = `
    <div class="sheet">
      <div class="head">
        <div class="brand">
          <img class="logo" src="${HAZZA_LOGO}" alt="Hazza Institute of Technology" />
          <div class="brand-text">
            <strong>Hazza Institute of Technology</strong>
            <span>Consolidated Hostel Invoice</span>
          </div>
          ${hostelProfileImage ? `<img class="hostel-pic" src="${hostelProfileImage}" alt="${hostelName}" />` : ''}
        </div>
        <div class="meta">
          <div class="inv-no">${hostelName}</div>
          <div>Period: <strong>${fmtDate(period_start)} – ${fmtDate(period_end)}</strong></div>
          <div>Residents: <strong>${invoices.length}</strong></div>
        </div>
      </div>

      <table class="items${isWide ? ' wide' : ''}">
        <thead>
          <tr><th>#</th><th>Resident</th><th>CNIC</th><th>Invoice #</th><th class="amt">Base</th>${showMeals ? '<th class="amt">Meals</th>' : ''}${showDeduction ? '<th class="amt">Deduction</th>' : ''}<th class="amt">Adj.</th><th class="amt">Total</th>${summaryHeadCells}</tr>
        </thead>
        <tbody>
          ${rows || `<tr><td colspan="${colCount}" style="text-align:center;color:#94a0bf;">No residents to invoice</td></tr>`}
          <tr class="total-row"><td colspan="${totalRowLeadColspan}">Grand Total</td><td class="amt">${fmtMoney(grand_total)}</td>${trailingColspan > 0 ? `<td colspan="${trailingColspan}"></td>` : ''}</tr>
        </tbody>
      </table>

      ${detailSections}

      <div class="foot-note">Generated by Hazza Institute of Technology — Hostel Management System</div>
    </div>`;

  openAndPrint(shell(`Consolidated Invoice — ${hostelName}`, body, isWide ? WIDE_STYLES : ''), autoPrint !== false);
}

// Same "View without auto-printing" wrapper as viewIndividualInvoice, for
// the consolidated/bulk invoice sheet.
export function viewBulkInvoice(args) {
  return printBulkInvoice({ ...args, autoPrint: false });
}

// ── Bulk invoice — CSV ───────────────────────────────────────────────
export function exportBulkInvoiceCSV({ hostelName, period_start, period_end, invoices, grand_total, attendanceRows = [], mealRows = [], includeAttendance, includeMeals, includeDaysSummary, includeMealsSummary, showMeals = true, showDeduction = true }) {
  const rows = [];
  rows.push([`Consolidated Hostel Invoice — ${hostelName}`]);
  rows.push([`Period: ${fmtDate(period_start)} to ${fmtDate(period_end)}`]);
  rows.push([`Residents: ${invoices.length}`]);
  rows.push([]);
  const header = ['#', 'Resident', 'CNIC', 'Invoice #', 'Base'];
  if (showMeals) header.push('Meals');
  if (showDeduction) header.push('Deduction');
  header.push('Adjustment', 'Total');
  if (includeDaysSummary) header.push('Present Days', 'Absent Days', 'Total Days');
  if (includeMealsSummary) header.push('Breakfast Served', 'Lunch Served', 'Dinner Served');
  rows.push(header);
  invoices.forEach((inv, i) => {
    const row = [i + 1, inv.admission.full_name, inv.admission.cnic || '', inv.invoice_number, inv.calculation.base_amount];
    if (showMeals) row.push(inv.calculation.meal_amount);
    if (showDeduction) row.push(-inv.calculation.deduction_amount);
    row.push(inv.calculation.adjustment_amount, inv.calculation.total_amount);
    if (includeDaysSummary) row.push(inv.calculation.present_days ?? 0, inv.calculation.absent_days ?? 0, inv.calculation.total_days ?? 0);
    if (includeMealsSummary) {
      const mc = mealCounts(inv.admission.id, mealRows);
      row.push(mc.breakfast, mc.lunch, mc.dinner);
    }
    rows.push(row);
  });
  const totalColIdx = header.indexOf('Total');
  const grandTotalRow = new Array(header.length).fill('');
  grandTotalRow[totalColIdx - 1] = 'Grand Total';
  grandTotalRow[totalColIdx] = grand_total;
  rows.push(grandTotalRow);

  if (includeAttendance && attendanceRows.length) {
    rows.push([]); rows.push(['Attendance Detail']);
    rows.push(['Date', 'Resident', 'Status', 'Marked By']);
    attendanceRows.forEach(r => rows.push([fmtDate(r.attendance_date), r.full_name, r.status, r.marked_by_name || r.marked_by || '']));
  }
  if (includeMeals && mealRows.length) {
    rows.push([]); rows.push(['Meal Detail']);
    rows.push(['Date', 'Resident', 'Breakfast', 'Lunch', 'Dinner']);
    mealRows.forEach(r => rows.push([fmtDate(r.meal_date), r.full_name, r.breakfast ? 'Yes' : 'No', r.lunch ? 'Yes' : 'No', r.dinner ? 'Yes' : 'No']));
  }

  downloadCsv(`invoices-${hostelName.replace(/\s+/g, '-')}-${period_start}_to_${period_end}.csv`, rows);
}
